import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { trackSignUp } from '../utils/analytics';
import useSocialProof, { niceCount } from '../utils/useSocialProof';
import safeStorage from '../utils/safeStorage';

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('form');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { register, verifyOtp, sendLoginOtp } = useAuth();
  const socialProof = useSocialProof();
  const familiesLabel = socialProof ? niceCount(socialProof.familiesTotal) : null;
  const navigate = useNavigate();
  const location = useLocation();
  const registerParams = new URLSearchParams(location.search);
  const nextPath = registerParams.get('next') || registerParams.get('redirect') || null;
  const emailParam = new URLSearchParams(location.search).get('email') || '';

  useEffect(() => {
    if (emailParam) setEmail(emailParam);
  }, [emailParam]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await register(name, email);
      setStep('otp');
      setCooldown(60);
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed.');
    } finally { setLoading(false); }
  };

  // After the account is created and the user is on the OTP step, "Resend code"
  // must use the login endpoint (which re-sends OTP to an existing user) rather
  // than re-calling register(), which would fail with "Email already registered".
  const handleResendOtp = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await sendLoginOtp(email);
      setCooldown(60);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend code. Please try again.');
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await verifyOtp(email, otp);
      trackSignUp('email');
      const pendingToken = safeStorage.getItem('pendingGiftToken');
      if (pendingToken) {
        safeStorage.removeItem('pendingGiftToken');
        navigate(`/gift/redeem/${pendingToken}`);
      } else if (nextPath && nextPath.startsWith('/') && !nextPath.startsWith('//')) {
        navigate(nextPath);
      } else {
        navigate('/onboarding');
      }
    } catch (err) {
      if (err.response?.data?.suspended) {
        const reason = encodeURIComponent(err.response.data?.message || '');
        navigate(`/suspended?reason=${reason}`);
        return;
      }
      setError(err.response?.data?.message || 'Invalid or expired OTP.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-vh-100 d-flex" style={{background:'linear-gradient(135deg,#1e1048 0%,#2d1b69 60%,#4a2a9e 100%)'}}>
      <Helmet>
        <title>Sign Up | Dreamy Bedtime</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Left panel — brand & value props */}
      <div className="d-none d-lg-flex flex-column justify-content-center p-5 col-lg-6"
        style={{position:'relative', overflow:'hidden'}}>
        <div style={{position:'absolute', top:'-80px', left:'-80px', width:'300px', height:'300px', borderRadius:'50%', background:'rgba(255,255,255,0.04)'}}></div>
        <div style={{position:'absolute', bottom:'-60px', right:'-60px', width:'250px', height:'250px', borderRadius:'50%', background:'rgba(255,255,255,0.04)'}}></div>

        <Link to="/" className="text-decoration-none d-block mb-5">
          <span style={{fontFamily:"'Fredoka One',cursive", fontSize:'36px', color:'white', letterSpacing:'0.5px'}}>
            🌙 Dreamy Bedtime
          </span>
        </Link>

        <h2 className="fw-bold mb-2" style={{color:'white', fontFamily:"'Fredoka One',cursive", fontSize:'36px', lineHeight:'1.2'}}>
          Bedtime stories<br />your kids will love
        </h2>
        <p style={{color:'rgba(255,255,255,0.65)', fontSize:'16px', marginBottom:'40px'}}>
          Join thousands of families creating magical moments every night.
        </p>

        {/* Free plan highlights */}
        <div className="p-4 rounded-4 mb-4" style={{background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.15)'}}>
          <div className="fw-bold mb-3" style={{color:'white', fontSize:'15px'}}>
            <i className="bi bi-gift-fill me-2" style={{color:'#f9c74f'}}></i>Free plan includes:
          </div>
          <div className="d-flex flex-column gap-2">
            {[
              '5 personalised stories every month',
              'Magical illustration with every story',
              'Read-aloud with browser voice',
              '1 child profile',
              'Story library with favourites',
            ].map(item => (
              <div key={item} className="d-flex align-items-center gap-2">
                <i className="bi bi-check-circle-fill" style={{color:'#4ade80', fontSize:'15px', flexShrink:0}}></i>
                <span style={{color:'rgba(255,255,255,0.85)', fontSize:'14px'}}>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Real family count from the DB — hidden until it crosses the
            first marketing tier so we never show tiny/fake numbers */}
        {familiesLabel && (
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex" style={{marginLeft:'-8px'}}>
              {['👩','👨','👩‍🦱','👨‍🦰','👩‍🦳'].map((e, i) => (
                <div key={i} className="rounded-circle border border-white d-flex align-items-center justify-content-center"
                  style={{width:'32px', height:'32px', background:'#6f42c1', fontSize:'16px', marginLeft: i === 0 ? 0 : '-8px', zIndex: 5-i}}>
                  {e}
                </div>
              ))}
            </div>
            <span style={{color:'rgba(255,255,255,0.7)', fontSize:'13px'}}>
              Join <strong style={{color:'white'}}>{familiesLabel}</strong> families already using Dreamy Bedtime
            </span>
          </div>
        )}
      </div>

      {/* Right panel — form */}
      <div className="d-flex align-items-center justify-content-center p-4 p-lg-5 flex-grow-1"
        style={{background:'var(--page-bg)', minHeight:'100vh'}}>
        <div style={{width:'100%', maxWidth:'400px'}}>

          {/* Mobile logo */}
          <div className="d-lg-none text-center mb-4">
            <Link to="/" className="text-decoration-none">
              <span style={{fontFamily:"'Fredoka One',cursive", fontSize:'28px', color:'var(--text-main)'}}>🌙 Dreamy Bedtime</span>
            </Link>
          </div>

          <h3 className="fw-bold mb-1" style={{color:'var(--text-main)', fontFamily:"'Fredoka One',cursive", fontSize:'28px'}}>
            {step === 'form' ? 'Create your account' : 'Check your email'}
          </h3>
          <p className="text-muted small mb-4">
            {step === 'form'
              ? 'Free forever — no credit card required.'
              : <>We sent a 6-digit code to <strong>{email}</strong>.</>}
          </p>

          {error && (
            <div className="alert alert-danger py-2 small rounded-3 d-flex align-items-center gap-2">
              <i className="bi bi-exclamation-circle-fill"></i>{error}
            </div>
          )}

          {step === 'form' ? (
            <form onSubmit={handleSendOtp}>
              <div className="mb-3">
                <label className="form-label fw-semibold small" style={{color:'var(--text-main)'}}>Your Name</label>
                <input type="text" className="form-control form-control-lg" placeholder="Jane Smith"
                  style={{borderRadius:'12px'}}
                  value={name} onChange={e => setName(e.target.value)} required />
              </div>
              <div className="mb-4">
                <label className="form-label fw-semibold small" style={{color:'var(--text-main)'}}>Email Address</label>
                <input type="email" className="form-control form-control-lg" placeholder="you@example.com"
                  style={{borderRadius:'12px'}}
                  value={email} onChange={e => setEmail(e.target.value)} required />
                <div className="mt-1 small text-muted">
                  <i className="bi bi-shield-lock me-1"></i>No password needed — we use secure email codes.
                </div>
              </div>
              <button type="submit" className="btn btn-dream btn-lg w-100 rounded-pill fw-bold" disabled={loading}>
                {loading
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending code…</>
                  : <><i className="bi bi-envelope me-2"></i>Get Started Free</>}
              </button>
              <p className="text-center text-muted mt-3 mb-0" style={{fontSize:'12px'}}>
                By signing up you agree to our{' '}
                <Link to="/terms" style={{color:'#6f42c1'}}>Terms</Link> and{' '}
                <Link to="/privacy" style={{color:'#6f42c1'}}>Privacy Policy</Link>.
              </p>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp}>
              <div className="mb-4">
                <label className="form-label fw-semibold small" style={{color:'var(--text-main)'}}>Verification Code</label>
                <input type="text" className="form-control form-control-lg text-center fw-bold"
                  placeholder="000 000" maxLength={6} inputMode="numeric"
                  style={{borderRadius:'12px', letterSpacing:'6px', fontSize:'24px'}}
                  value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))} required />
                <div className="d-flex justify-content-between mt-2">
                  <span className="small text-muted">Check your spam folder too</span>
                  <button type="button" className="btn btn-link btn-sm p-0"
                    style={{color: cooldown > 0 ? '#9ca3af' : '#6f42c1', fontSize:'13px'}}
                    onClick={handleResendOtp} disabled={loading || cooldown > 0}>
                    {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                  </button>
                </div>
              </div>
              <button type="submit" className="btn btn-dream btn-lg w-100 rounded-pill fw-bold mb-3" disabled={loading}>
                {loading
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Creating account…</>
                  : <><i className="bi bi-check2-circle me-2"></i>Verify & Create Account</>}
              </button>
              <button type="button" className="btn btn-outline-secondary w-100 rounded-pill"
                onClick={() => { setStep('form'); setOtp(''); setError(''); }}>
                <i className="bi bi-arrow-left me-1"></i>Go back
              </button>
            </form>
          )}

          <div className="text-center mt-4 pt-3" style={{borderTop:'1px solid #f0eaf8'}}>
            <span className="text-muted small">Already have an account? </span>
            <Link to="/login" className="fw-bold text-decoration-none small" style={{color:'#6f42c1'}}>
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
